import { getToken } from '@/constants/auth';

/**
 * API base URL — works in both development and production.
 * - Development: set VITE_API_BASE_URL in .env (e.g. your local path like .../Kenya_Friars/backend/...)
 * - Production: set VITE_API_BASE_URL in .env.production (base URL + /backend, e.g. https://kenyafriars.org/backend/...)
 * Dev-only fallback avoids requiring .env for local runs.
 */
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env?.VITE_API_BASE_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return envUrl.trim();
  }
  if (import.meta.env.DEV) {
    return 'http://localhost/Kenya_Friars/backend/api.php?route=';
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();

/** Shared API base for all backend requests. Use this instead of hardcoding URLs. */
export const API_BASE = API_BASE_URL;

/**
 * Backend uploads base URL (for media). Derived from API base.
 * e.g. .../backend/api.php?route= → .../backend/uploads/
 */
export const UPLOADS_BASE =
  API_BASE_URL.replace(/api\.php\?route=.*$/, 'uploads/').replace(/\/+$/, '') + '/';

/** Headers to add to admin fetch calls (includes Bearer token when logged in). */
export function getAuthHeaders(): HeadersInit {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** fetch with auth header for admin routes. Use instead of fetch when calling protected API. */
export function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  const t = getToken();
  if (t) {
    headers.set('Authorization', `Bearer ${t}`);
    headers.set('X-Auth-Token', t);
  }
  return fetch(url, { ...init, headers });
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Don't set Content-Type for GET requests or if body is empty
    const defaultHeaders: HeadersInit = {};

    const token = getToken();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
      defaultHeaders['X-Auth-Token'] = token;
    }

    // Only set Content-Type for JSON body; omit for FormData (browser sets multipart boundary)
    const isFormData = options.body instanceof FormData;
    if (
      options.body &&
      (options.method === 'POST' || options.method === 'PUT' || options.method === 'PATCH') &&
      !isFormData
    ) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      // Use credentials: include for CORS with cookies if needed
      credentials: import.meta.env.DEV ? 'include' : 'omit',
    };

    try {
      const response = await fetch(url, config);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Try to parse error response
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.message || 'An error occurred');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      if (searchParams.toString()) {
        // baseUrl already contains ? (e.g. api.php?route=), so use & for extra params
        url += (this.baseUrl.includes('?') ? '&' : '?') + searchParams.toString();
      }
    }
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body as BodyInit,
    });
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, params?: Record<string, string | number>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      if (searchParams.toString()) {
        url += (this.baseUrl.includes('?') ? '&' : '?') + searchParams.toString();
      }
    }
    return this.request<T>(url, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
